import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Mail, Shield, UserCheck, UserMinus, UserPlus, Users } from "lucide-react";
import PageShell from "../../components/dashboard/PageShell";
import FilteredDataTable from "../../components/dashboard/FilteredDataTable";
import StatusBadge from "../../components/dashboard/StatusBadge";
import StatCard from "../../components/dashboard/StatCard";
import StatCardGrid from "../../components/dashboard/StatCardGrid";
import InviteOpsUserModal from "../../components/dashboard/InviteOpsUserModal";
import ManageOpsUserModal from "../../components/dashboard/ManageOpsUserModal";
import { AvatarName } from "../../components/dashboard/tableCellContent";
import Button from "../../components/ui/Button";
import { USE_MOCK_AUTH } from "../../config/api";
import { OPS_PERMISSIONS as P } from "../../config/permissions";
import { TEAM_MEMBERS_FILTER_CONFIG } from "../../config/tableFilterConfigs";
import usePermissions from "../../hooks/usePermissions";
import { useLiveEventTick } from "../../hooks/useLiveRefresh";
import {
  clearTeamMembersCache,
  deleteTeamMember,
  fetchTeamMembers,
  registerTeamMember,
  restoreTeamMember,
  updateTeamMember,
  updateTeamMemberStatus,
} from "../../services/teamMembersApi";
import { clearOpsRolesCache, fetchOpsRoles } from "../../services/rolesApi";
import { showError, showSuccess } from "../../utils/toast";
import {
  getRoleDisplayLabel,
  getRolePermissions,
  getRolesSource,
  hydrateRolesFromApi,
  resolveRoleKeyFromSlug,
  subscribeRolePermissions,
} from "../../data/opsRolePermissionsStore";
import {
  deleteOpsUser,
  inviteOpsUser,
  listOpsUsers,
  updateOpsUser,
} from "../../data/opsUsersStore";

function formatLastLogin(value) {
  if (!value) return "—";
  const raw = String(value);
  if (raw.length >= 16) return `${raw.slice(0, 10)} ${raw.slice(11, 16)}`;
  return raw.slice(0, 10);
}

function withResolvedRole(member) {
  if (!member) return member;
  const roleKey =
    member.role ||
    resolveRoleKeyFromSlug(member.role_slug) ||
    member.role_slug ||
    null;
  return {
    ...member,
    role: roleKey,
    permissions:
      member.permissions?.length > 0
        ? member.permissions
        : roleKey
          ? getRolePermissions(roleKey)
          : [],
  };
}

export default function TeamPage() {
  const { hasPermission, isSuperAdmin } = usePermissions();
  const liveTeamTick = useLiveEventTick(/^(role\.|team\.)/);
  const token = useSelector((state) => state.auth.token);
  const [portalUsers, setPortalUsers] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [manageUser, setManageUser] = useState(null);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState(null);
  const [rolesReady, setRolesReady] = useState(() => getRolesSource() === "api" || USE_MOCK_AUTH);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;

  const canInvite = hasPermission(P.OPS_USERS_INVITE) || isSuperAdmin;
  const canManage = hasPermission(P.OPS_USERS_MANAGE) || isSuperAdmin;

  useEffect(() => {
    if (useLiveApi) return undefined;
    return subscribeRolePermissions(() => {
      setPortalUsers(listOpsUsers().map(withResolvedRole));
    });
  }, [useLiveApi]);

  useEffect(() => {
    let cancelled = false;
    async function loadRoles() {
      if (!useLiveApi) {
        setRolesReady(true);
        return;
      }
      try {
        clearOpsRolesCache();
        const apiRoles = await fetchOpsRoles(token);
        if (cancelled) return;
        hydrateRolesFromApi(apiRoles || []);
        setRolesReady(true);
        setPortalUsers((prev) => prev.map(withResolvedRole));
      } catch (err) {
        if (!cancelled) {
          showError(err.message || "Could not load roles for assignment.");
          setRolesReady(false);
        }
      }
    }
    loadRoles();
    return () => {
      cancelled = true;
    };
  }, [token, useLiveApi]);

  const refreshTeamMembers = useCallback(async () => {
    if (!useLiveApi) {
      const local = listOpsUsers().map(withResolvedRole);
      setPortalUsers(local);
      return local;
    }
    clearTeamMembersCache();
    const members = (await fetchTeamMembers(token) || []).map(withResolvedRole);
    setPortalUsers(members);
    return members;
  }, [token, useLiveApi]);

  const loadTeamMembers = useCallback(async () => {
    setTeamLoading(true);
    setTeamError(null);
    try {
      if (useLiveApi) {
        const members = (await fetchTeamMembers(token) || []).map(withResolvedRole);
        setPortalUsers(members);
      } else {
        setPortalUsers(listOpsUsers().map(withResolvedRole));
      }
    } catch (err) {
      setTeamError(err.message || "Could not load team members.");
      showError(err.message || "Could not load team members.");
      setPortalUsers([]);
    } finally {
      setTeamLoading(false);
    }
  }, [token, useLiveApi]);

  useEffect(() => {
    loadTeamMembers();
  }, [loadTeamMembers]);

  useEffect(() => {
    if (!liveTeamTick) return undefined;
    refreshTeamMembers().catch((err) => {
      showError(err.message || "Could not refresh team members.");
    });
    return undefined;
  }, [liveTeamTick, refreshTeamMembers]);

  const stats = useMemo(() => {
    const active = portalUsers.filter(
      (u) => !u.deleted_at && (u.status === "active" || u.status === true)
    ).length;
    const inactive = portalUsers.filter(
      (u) =>
        !u.deleted_at &&
        (u.status === "inactive" || u.status === false || u.status === "suspended")
    ).length;
    const invited = portalUsers.filter((u) => u.status === "invited").length;
    return { active, inactive, invited, total: portalUsers.length };
  }, [portalUsers]);

  const portalColumns = [
    { key: "numericId", header: "ID", type: "id" },
    {
      key: "name",
      header: "Team member",
      type: "name",
      render: (row) => <AvatarName name={row.name} sub={row.email} />,
    },
    {
      key: "role",
      header: "Role",
      type: "badge",
      render: (row) => (
        <span className="rounded-lg bg-primary/[0.04] px-2 py-1 text-[11px] font-semibold text-primary/70 ring-1 ring-primary/[0.06]">
          {getRoleDisplayLabel(row.role)}
        </span>
      ),
    },
    {
      key: "phone",
      header: "Phone",
      type: "text",
      render: (row) => (
        <span className="text-xs font-medium text-primary/55">
          {row.phone || row.phone_number || "—"}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      type: "badge",
      render: (row) => (
        <StatusBadge
          status={
            row.deleted_at || row.status === "deleted"
              ? "closed"
              : row.status === "active" || row.status === true
                ? "active"
                : row.status === "suspended" || row.status === "inactive"
                  ? "closed"
                  : "pending"
          }
          label={
            row.deleted_at || row.status === "deleted"
              ? "Deleted"
              : row.status === "active" || row.status === true
                ? "Active"
                : row.status === "suspended"
                  ? "Suspended"
                  : row.status === "inactive"
                    ? "Inactive"
                    : "Invited"
          }
        />
      ),
    },
    { key: "invitedAt", header: "Added", type: "date" },
    {
      key: "last_login_at",
      header: "Last login",
      type: "text",
      render: (row) => (
        <span className="text-xs font-medium text-primary/55">
          {formatLastLogin(row.last_login_at)}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: (row) =>
        canManage ? (
          <div className="flex flex-nowrap items-center gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setManageUser(row);
              }}
            >
              <UserCheck className="h-3.5 w-3.5" />
              Manage
            </Button>
          </div>
        ) : null,
    },
  ];

  const handleInvite = async (payload) => {
    try {
      if (!useLiveApi) {
        inviteOpsUser(payload);
        setPortalUsers(listOpsUsers());
        showSuccess(`Invite sent to ${payload.email}.`);
        return;
      }
      await registerTeamMember(token, payload);
      await refreshTeamMembers();
      showSuccess(`Operator created. Welcome email sent to ${payload.email}.`);
    } catch (err) {
      showError(err.message || "Could not onboard team member.");
      throw err;
    }
  };

  const handleUpdateMember = async (payload) => {
    try {
      const nextStatus = payload.status;
      if (!useLiveApi) {
        updateOpsUser(manageUser.id, {
          first_name: payload.first_name,
          last_name: payload.last_name,
          name: `${payload.first_name} ${payload.last_name}`.trim(),
          email: payload.email,
          phone_number: payload.phone_number,
          role: payload.role,
          permissions: getRolePermissions(payload.role),
          status: nextStatus || manageUser.status,
        });
        setPortalUsers(listOpsUsers());
        showSuccess("Team member updated.");
        return;
      }
      await updateTeamMember(token, payload);
      if (nextStatus === "active" || nextStatus === "inactive") {
        const current =
          manageUser.status === true || manageUser.status === "active" ? "active" : "inactive";
        if (nextStatus !== current) {
          await updateTeamMemberStatus(token, payload.admin_slug, nextStatus);
        }
      }
      await refreshTeamMembers();
      showSuccess("Team member updated.");
    } catch (err) {
      showError(err.message || "Could not update team member.");
      throw err;
    }
  };

  const handleDeleteMember = async (user) => {
    try {
      if (!useLiveApi) {
        const removed = deleteOpsUser(user.id);
        if (!removed) {
          throw new Error("This account cannot be deleted.");
        }
        setPortalUsers(listOpsUsers());
        showSuccess("Team member deleted.");
        return;
      }
      const slug = user.admin_slug || user.id;
      await deleteTeamMember(token, slug);
      await refreshTeamMembers();
      showSuccess("Team member deleted.");
    } catch (err) {
      showError(err.message || "Could not delete team member.");
      throw err;
    }
  };

  const handleRestoreMember = async (user) => {
    try {
      if (!useLiveApi) {
        updateOpsUser(user.id, { deleted_at: null, status: "active" });
        setPortalUsers(listOpsUsers());
        showSuccess("Team member restored.");
        return;
      }
      await restoreTeamMember(token, user.admin_slug || user.id);
      await refreshTeamMembers();
      showSuccess("Team member restored.");
    } catch (err) {
      showError(err.message || "Could not restore team member.");
      throw err;
    }
  };

  return (
    <PageShell
      title="Team management"
      description="Onboard ops staff by role and manage their account details."
      actions={
        canInvite ? (
          <Button size="sm" onClick={() => setInviteOpen(true)} disabled={!rolesReady}>
            <UserPlus className="h-4 w-4" />
            {rolesReady ? "Onboard team member" : "Loading roles…"}
          </Button>
        ) : null
      }
    >
      {teamLoading ? (
        <div className="mb-3 rounded-xl border border-primary/[0.07] bg-primary/[0.02] px-4 py-3 text-xs text-primary/50">
          Loading team members…
        </div>
      ) : null}
      {teamError && !teamLoading ? (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-800">
          <span>{teamError}</span>
          <Button variant="ghost" size="sm" onClick={loadTeamMembers}>
            Retry
          </Button>
        </div>
      ) : null}
      {!teamLoading && !teamError && portalUsers.length === 0 ? (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
          <span>The server returned no team members.</span>
          <Button variant="ghost" size="sm" onClick={loadTeamMembers}>
            Reload
          </Button>
        </div>
      ) : null}

      <StatCardGrid cols={4}>
        <StatCard label="Team members" value={stats.total} icon={Users} tone="primary" />
        <StatCard label="Active" value={stats.active} icon={UserCheck} tone="emerald" />
        <StatCard label="Inactive" value={stats.inactive} icon={UserMinus} tone="amber" />
        <StatCard
          label="Pending invites"
          value={stats.invited}
          icon={Mail}
          tone={stats.invited ? "amber" : "emerald"}
        />
      </StatCardGrid>

      <div className="mb-4 rounded-xl border border-primary/10 bg-gradient-to-r from-primary/[0.03] to-accent/[0.05] px-4 py-3">
        <div className="flex items-start gap-2">
          <Shield className="mt-0.5 h-4 w-4 shrink-0 text-primary/50" />
          <p className="text-xs text-primary/55">
            Onboard ops staff with a role. They receive a welcome email and inherit that role’s
            permissions. Manage profile, status, and restore deleted members from the row actions.
          </p>
        </div>
      </div>

      <FilteredDataTable
        data={portalUsers}
        columns={portalColumns}
        config={TEAM_MEMBERS_FILTER_CONFIG}
        pageSize={8}
      />

      {inviteOpen && (
        <InviteOpsUserModal onClose={() => setInviteOpen(false)} onInvite={handleInvite} />
      )}

      {manageUser && (
        <ManageOpsUserModal
          user={manageUser}
          onClose={() => setManageUser(null)}
          onSave={handleUpdateMember}
          onDelete={handleDeleteMember}
          onRestore={handleRestoreMember}
        />
      )}
    </PageShell>
  );
}
